import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stepper,
    Step,
    StepLabel,
    Typography,
    Box,
    Badge,
    Chip,
    IconButton,
    Tooltip,
    Alert,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import ProductSelectionStep from './ProductSelectionStep';
import BundleDetailsStep from './BundleDetailsStep';
import BundleImageSelectionStep from './BundleImageSelectionStep';

const Bundles = ({ api, isAuthenticated }) => {
    const [products, setProducts] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [openBundleDialog, setOpenBundleDialog] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [newBundle, setNewBundle] = useState({
        id: null,
        name: '',
        productIds: [],
        discount: '',
        sku: '',
        costPrice: 0,
        mainImage: '',
        bundleImages: [],
        bundleType: 'flexible',
    });
    const [editMode, setEditMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshingId, setRefreshingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [productAttributes, setProductAttributes] = useState({});
    const [bundleError, setBundleError] = useState(null);

    // Tolerates both the new [{productId, qty}] shape and the legacy plain-id-array shape.
    const parseBundleChildren = (productIds) => {
        const parsed = typeof productIds === 'string'
            ? (() => { try { return JSON.parse(productIds); } catch { return []; } })()
            : (Array.isArray(productIds) ? productIds : []);
        return parsed.map(entry => (entry && typeof entry === 'object')
            ? { productId: entry.productId, qty: Math.max(1, parseInt(entry.qty, 10) || 1) }
            : { productId: entry, qty: 1 });
    };

    useEffect(() => {
        if (isAuthenticated) {
            getBundles();
            getProducts();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        const ids = [...new Set(bundles.flatMap(b => parseBundleChildren(b.product_ids).map(c => c.productId)))];
        if (ids.length === 0) {
            setProductAttributes({});
            return;
        }
        api.get(`/api/products/attributes?ids=${ids.join(',')}`)
            .then(response => setProductAttributes(response.data.products))
            .catch(error => console.error(error));
    }, [bundles]);

    const [validationFunction, setValidationFunction] = useState(null);

    const getBundles = () => {
        api.get('/api/bundles')
            .then(response => setBundles(response.data.bundles))
            .catch(error => console.error(error));
    };

    const getProducts = () => {
        api.get('/api/products')
            .then(response => {
                setProducts(response.data.items);
            })
            .catch(error => console.error(error));
    };

    const handleCreateOrUpdateBundle = () => {
        const method = editMode ? 'put' : 'post';
        const url = editMode ? `/api/bundles/${newBundle.id}` : '/api/bundles';

        setSaving(true);
        setBundleError(null);
        api[method](url, {
            name: newBundle.name,
            productIds: newBundle.productIds,
            discount: parseFloat(newBundle.discount),
            costPrice: parseFloat(newBundle.costPrice),
            sku: newBundle.sku,
            mainImage: newBundle.mainImage,
            bundleImages: newBundle.bundleImages,
            bundleType: newBundle.bundleType || 'flexible',
        })
            .then(response => {
                if (editMode) {
                    setBundles(bundles.map(b => (b.id === newBundle.id ? response.data : b)));
                } else {
                    setBundles([...bundles, response.data]);
                }
                getProducts();
                handleClose();
            })
            .catch(error => {
                const msg = error.response?.data?.errorMessage
                    || error.response?.data?.error
                    || error.message
                    || 'Failed to save bundle. Please try again.';
                setBundleError(msg);
                console.error(error);
            })
            .finally(() => setSaving(false));
    };

    const handleDeleteBundle = (bundleId) => {
        if (confirm('Are you sure you want to delete this bundle?')) {
            api.delete(`/api/bundles/${bundleId}`)
                .then(() => setBundles(bundles.filter(b => b.id !== bundleId)))
                .catch(error => console.error(error));
        }
    };

    const handleEditBundle = (bundle) => {
        setNewBundle({
            id: bundle.id,
            name: bundle.name,
            productIds: parseBundleChildren(bundle.product_ids),
            discount: bundle.discount,
            costPrice: bundle.costPrice,
            sku: bundle.sku,
            mainImage: bundle.mainImage || '',
            bundleImages: bundle.bundleImages || [],
            bundleType: bundle.bundle_type || 'flexible',
        });
        setEditMode(true);
        setOpenBundleDialog(true);
    };

    const handleRefreshStock = (bundleId) => {
        setRefreshingId(bundleId);
        api.post(`/api/bundles/${bundleId}/sync-stock`)
            .then((response) => {
                setBundles(bundles.map(b => (b.id === bundleId ? { ...b, stock: response.data.stock } : b)));
            })
            .catch(error => console.error(error))
            .finally(() => setRefreshingId(null));
    };

    const handleClose = () => {
        setOpenBundleDialog(false);
        setActiveStep(0);
        setSearchQuery('');
        setBundleError(null);
        setNewBundle({
            id: null,
            name: '',
            productIds: [],
            discount: '',
            costPrice: 0,
            sku: '',
            mainImage: '',
            bundleImages: [],
            bundleType: 'flexible',
        });
        setEditMode(false);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Bundles
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setOpenBundleDialog(true)} sx={{ mb: 2 }}>
                Create Bundle
            </Button>
            <TableContainer component={Paper}>
                <Table sx={{ '& td, & th': { padding: '8px' } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Products</TableCell>
                            <TableCell>Discount</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Stock</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bundles.map(bundle => {
                            const children = parseBundleChildren(bundle.product_ids);
                            return (
                            <TableRow key={bundle.id} sx={{ height: '40px', backgroundColor: products.some(p => p.id === bundle.ecwid_id && !p.inStock) ? '#ffebee' : 'inherit' }}>
                                <TableCell>
                                    {bundle.name}
                                    <Chip
                                        label={bundle.bundle_type === 'fixed' ? 'Fixed' : 'Flexible'}
                                        size="small"
                                        variant="outlined"
                                        color={bundle.bundle_type === 'fixed' ? 'default' : 'primary'}
                                        sx={{ ml: 1, fontSize: '0.65rem' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {children.map(child => {
                                        const p = products.find(prod => prod.id === child.productId);
                                        if (!p) return null;
                                        const attributes = productAttributes[child.productId]?.attributes || [];
                                        return (
                                            <Box key={p.id} sx={{ display: 'block', marginRight: '8px' }}>
                                                <img style={{verticalAlign: 'middle'}} src={p.smallThumbnailUrl || 'https://placehold.co/50'} alt={p.name} width="40" height="40" />
                                                {p.name} x{child.qty} - <Chip
                                                label={p.quantity}
                                                color={p.inStock?'success':'error'}
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                                {attributes.length > 0 && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: '48px' }}>
                                                        {attributes.map(a => `${a.name}: ${a.value}`).join(' · ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </TableCell>
                                <TableCell>{bundle.discount}%</TableCell>
                                <TableCell>{products
                                    .filter(p => p.id === bundle.ecwid_id)
                                    .map(p => p.defaultDisplayedPriceFormatted)
                                    .join(', ')}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={bundle.stock >= 999999 ? 'Unlimited' : bundle.stock}
                                        color={bundle.stock === 0 ? 'error' : bundle.stock <= 1 ? 'warning' : 'success'}
                                        size="small"
                                    />
                                    <Tooltip title="Refresh stock from children">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRefreshStock(bundle.id)}
                                            disabled={refreshingId === bundle.id}
                                            sx={{ ml: 0.5 }}
                                        >
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Button onClick={() => handleEditBundle(bundle)} color="primary">
                                        EDIT
                                    </Button>
                                    <Button onClick={() => handleDeleteBundle(bundle.id)} color="error">
                                        DELETE
                                    </Button>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openBundleDialog} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>{editMode ? 'Edit Bundle' : 'Create New Bundle'}</DialogTitle>
                <DialogContent>
                    {bundleError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBundleError(null)}>
                            {bundleError}
                        </Alert>
                    )}
                    <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
                        <Step><StepLabel>Select Products</StepLabel></Step>
                        <Step><StepLabel>Bundle Details</StepLabel></Step>
                        <Step><StepLabel>Select Images</StepLabel></Step>
                    </Stepper>
                    {activeStep === 0 && (
                        <ProductSelectionStep products={products} newBundle={newBundle} setNewBundle={setNewBundle} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                    )}
                    {activeStep === 1 && (
                        <BundleDetailsStep products={products} newBundle={newBundle} setNewBundle={setNewBundle} api={api} activeStep={activeStep} setValidationFunction={setValidationFunction} />
                    )}
                    {activeStep === 2 && (
                        <BundleImageSelectionStep products={products} newBundle={newBundle} setNewBundle={setNewBundle} api={api} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                    {activeStep > 0 && <Button onClick={() => setActiveStep(activeStep - 1)} disabled={saving}>Back</Button>}
                    {activeStep < 2 ? (
                        <Button
                            onClick={() => {
                                if (activeStep === 1) {
                                    if (validationFunction && validationFunction()) {
                                        setActiveStep(activeStep + 1);
                                    }
                                } else {
                                    setActiveStep(activeStep + 1);
                                }
                            }}
                            variant="contained"
                            color="primary"
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            onClick={handleCreateOrUpdateBundle}
                            variant="contained"
                            color="primary"
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {saving ? (editMode ? 'Updating…' : 'Creating…') : (editMode ? 'Update Bundle' : 'Create Bundle')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Bundles;
